// The contract every piece of chart furniture keeps (R548,
// ui-map-interface.md §17).
//
// This module is pure types. It imports the two generated packs and the
// engine's own type modules, and it opens no GL, no DOM and no socket, so a
// piece test reads it in jsdom.
//
// **One context, mutated in place** (R131). `ChartFurniture` holds one
// `ChartContext` and rewrites its fields on every frame; a piece reads it
// and keeps nothing out of it past the call. A frame therefore allocates
// nothing here.
//
// **A piece is either a batch piece or a DOM piece.** A `Piece` writes
// instances into the batches of its tier and draws on the canvas; a
// `DomPiece` mounts one element in the `#furniture` host and writes words
// into it. The canvas/DOM split of §1 is what decides which: a picture that
// follows the water is a `Piece`, a card of text that stands still is a
// `DomPiece`.
//
// **A piece is static or live.** A static piece packs its instances when one
// of its `rebuildOn` reasons fires and then costs one flush a frame; a live
// piece begins its sinks every frame and packs again. Nothing else is
// allowed: a piece that repacks every frame with no clock of its own is a
// static piece that forgot to name its reason.

import type { CHART, ChartPieceId } from '../../gen/presentation';
import type { CHART_READINGS } from '../../gen/balance';
import type { Site } from '../../gen/proto/aquaphobia';
import type { GameState, Selection } from '../../state';
import type { UIBeam } from '../../render/control-types';
import type { CornerGrid } from '../../ui/cornergrid';
import type { Feature, TerrainPlane } from '../../render/terrain-features';
import type { FeatureNames } from '../../render/feature-names';
import type { CameraUniforms, DrawCtx, FrameInfo, ViewRect } from '../layer';
import type { SmallMark } from '../smallmarks';
import type { CircleSink, QuadSink, SegmentSink } from '../layers/batches';
import type { Hit } from '../hit';
import type { HoldSymbolId } from '../../render/hold-marks';
import type { MapMarkerStyle } from '../../ui/settings';
import type { CurrentLook, FactionEdgeStyle, FactionKeyStyle } from '../../render/scene-look';
import type { DepthLook } from '../../depthlook';
import type { PoiSet } from '../../render/poi-marks';
import type { ChartGlyphs } from './glyphs';
import type { FlowGrid, FlowGridPatch } from './pieces/current-field';

export type { ChartPieceId };

/**
 * The stored pick, `off` included (`labels.toml` `chart_readings`, R548).
 *
 * `ui/settings.ts` exports the same shape. It is restated here because this
 * module is the one every piece imports, and a piece must not carry a path
 * into the settings store to name a reading.
 */
export type ChartSetting = (typeof CHART_READINGS)[number];

/** A reading that DRAWS. `off` is the absence of a reading, not one of them. */
export type ChartReading = Exclude<ChartSetting, 'off'>;

/** Which of the two engine layers a piece draws in. */
export type ChartTier = 'sea' | 'screen';

/** How often a piece packs: on a reason, or on every frame. */
export type ChartPack = 'static' | 'live';

/**
 * What a replay film does to a piece (R531, R548).
 *
 * `keep` is the terrain furniture — the isobaths, the place names, the
 * ribbons, the bottom nature, the hazards and the banks. That is the sea the
 * fight happened in, and it stays under a film. The margin warning and the
 * neat line keep too: they are the sheet the sea is drawn on (R802). `hide` is everything that
 * belongs to the season running now: it goes while the film has the glass.
 */
export type ChartFilm = 'keep' | 'hide';

/**
 * Why a piece must pack again.
 *
 * A piece names the reasons it answers in `rebuildOn`, and the controller
 * marks a reason when the thing behind it moved. A piece that names no
 * reason packs one time and never again.
 */
export type RebuildReason =
  | 'hello' | 'owners' | 'flip' | 'sites' | 'state' | 'sieges' | 'clan'
  | 'inbox' | 'selection' | 'hover' | 'zoomband' | 'reading' | 'settings'
  | 'font' | 'layout' | 'resize' | 'minute' | 'wrecks' | 'quake' | 'atlas'
  | 'markers' | 'look' | 'heat' | 'tide' | 'survey' | 'seamounts';

/** What a pick on a piece answers with: one mark, and where it stands. */
export interface PickHit {
  kind: 'mark';
  /** The piece's own id for the thing under the pointer. */
  id: string;
  /** Where the mark is, in world px. The nearest one wins a pick. */
  x: number;
  y: number;
}

/** The three sinks of one tier and one pack. */
export interface ChartSinks {
  quads: QuadSink;
  segments: SegmentSink;
  circles: CircleSink;
}

/** The one method the chart needs of a site layer. */
export interface ChartSites {
  get(siteId: number): Site | undefined;
}

/**
 * What the chart reads of the map scene.
 *
 * It is a structural interface and not the `MapScene` class, so a piece
 * carries no path into the fifteen layers and a test hands over a plain
 * object. `MapScene` satisfies it.
 */
export interface ChartScene {
  /** The commander's own hold, in world px. */
  readonly home: { x: number; y: number };
  /** The viewer's squad, as account ids (R199). */
  readonly squadIds: ReadonlySet<number>;
  /** One entry per working sovereignty source (R116). */
  readonly beams: readonly UIBeam[];
  /** The owner byte of every cell. */
  readonly owners: Uint8Array;
  /** The blocked mask, or null before the first hello. */
  readonly blocked: Uint8Array | null;
  /** Cells carrying a base: uncapturable (R66). */
  readonly baseCells: ReadonlySet<number>;
  /** Cells carrying a tier-2 strategic object. */
  readonly structureCells: ReadonlySet<number>;
  /** The challenger and the gauge of every contested cell. */
  readonly contestByCell: ReadonlyMap<number, { challenger: number; gauge: number }>;
  /** The sovereignty gauge of every cell, ×10 (§8). */
  readonly gaugeByCell: ReadonlyMap<number, number>;
  /** The listening circles the server says this commander receives (R112). */
  readonly ringsPx: readonly { x: number; y: number; range: number }[];
  /** The site layer of the running world, or null before the hello. */
  readonly sites: ChartSites | null;
  /**
   * The hold symbol the player picked (R633), or nothing on a scene that
   * draws no hold.
   *
   * The hold site of card 64 draws the BROKEN hold of the sites layer in
   * the historical reading, so it must ask for the same atlas cell the
   * layer asks for. This gives the symbol. A scene that states none takes
   * `DEFAULT_HOLD_SYMBOL`, which is the stored default.
   */
  readonly holdSymbol?: HoldSymbolId;
  /**
   * The map marker look the player picked (R1291), or nothing on a scene
   * that states none. The map markers (card 66) draw in it, and a scene
   * that states none takes the AUTO look. `MapScene.setLook` marks the
   * `look` reason when it changes.
   */
  readonly mapMarker?: MapMarkerStyle;
  /**
   * The faction key look the player picked (R1568), or nothing on a scene
   * that states none. The faction key (card 67) stands while it is `key`,
   * and a scene that states none takes the AUTO look, which is `key`.
   * `MapScene.setLook` marks the `look` reason when it changes.
   */
  readonly factionKey?: FactionKeyStyle;
  /**
   * The faction edge look the player picked (R1568, R3277), or nothing on
   * a scene that states none. The faction key draws the border sample of
   * each row in it: the `limit` edge draws its thin line and its band, and
   * a scene that states none takes the AUTO look, `dash`.
   * `MapScene.setLook` marks the `look` reason when it changes.
   */
  readonly factionEdge?: FactionEdgeStyle;
  /**
   * The active current look (R2716, R2869), or nothing on a scene that
   * states none: the World field `currents`, or `currentsHeat` while the
   * scene draws the current heat look. Card 07 and the speed field draw
   * in it, and a scene that states none takes the AUTO look.
   * `MapScene.setLook` and a change of the CURRENTS heat map mark the `look`
   * reason when it changes.
   */
  readonly currentLook?: CurrentLook;

  /**
   * The point of interest set the player picked (R2714), or nothing on a
   * scene that states none. The points of interest (card 69) draw in it,
   * and a scene that states none takes the AUTO set. `MapScene.setLook`
   * marks the `look` reason when it changes.
   */
  readonly poi?: PoiSet;
  /**
   * The reading the holds and the hull pips are drawn in (R633, R1568): the
   * chart reading, and the modern one under the setting `off`. The faction
   * key draws its badges in it, so a key never shows a shield beside a map
   * of roundels. A scene that states none leaves the key the drawn reading.
   */
  readonly markEra?: 'h' | 'm';
  /**
   * The fleet layer, or nothing on a scene that draws no hulls.
   *
   * It is optional because a test scene carries no hulls at all, and because
   * `MapScene` satisfies this member with the layer it already holds: the
   * chart states what it reads of a fleet here, and the layer answers it.
   */
  readonly fleets?: ChartFleets | null;
  /**
   * The terrain plane, or null while the prepare has not landed (§17.3.13).
   *
   * The isobaths are traced off the SAME raster the terrain composite draws
   * the ground from, so a contour and the rim it runs inside can never
   * disagree. It arrives two animation frames after the hello, so a piece
   * that reads it waits for it in its own `visible`.
   */
  readonly plane?: TerrainPlane | null;
  /**
   * The water-lining of card 01 (§17.3.01).
   *
   * The one piece with no batch. It writes four iso-lines into the terrain
   * program and three into the territory program, and draws nothing itself.
   * A page that carries no such layer takes the call and does nothing.
   */
  setWaterLines?(on: boolean, cfg: ChartLineCfg): void;
  /**
   * The depth tints of card 09 (§17.3.09, R2713).
   *
   * The piece owns no batch: it hands the band field and the reading to
   * the depth layer, one full-world quad. A page that carries no such
   * layer takes the call and does nothing.
   */
  setDepthField?(reading: ChartReading, bands: Uint8Array | null): void;
  /**
   * The speed field of the current looks (§17.3.07, R2827).
   *
   * The piece hands the flow grid of a new field, a survey or a new grid,
   * and the strongest current at tide factor 1, to the speed field layer:
   * one full-world quad. A page that carries no such layer takes the call
   * and does nothing.
   */
  setSpeedField?(grid: FlowGrid | null, strongest: number): void;
  /**
   * A survey pass moved the nodes of `patch` in the grid the layer holds,
   * and the strongest current held (R2897): the layer writes those nodes
   * and uploads their rows alone. A page without it takes a whole
   * `setSpeedField`.
   */
  patchSpeedField?(grid: FlowGrid, strongest: number, patch: FlowGridPatch): void;
  /**
   * The depth look the map draws (R2726): the World field `depth`, flipped
   * for the session by the key `\`, or nothing on a scene with no depth
   * layer. The legend card paints its band swatches in it.
   */
  readonly depthLook?: DepthLook;
  /**
   * The look the depth tints draw in under the current streaks (R2871), or
   * null while no tint draws there: the gate of card 09, the CURRENTS heat
   * map and the DEPTH heat map turn it off. Card 07 gives each mark the
   * alpha gain of its band while it holds a look. A scene that states none
   * draws no gain.
   */
  readonly tintLook?: DepthLook | null;
}

/** What one water-lining set states: a distance, an alpha and a width. */
export interface ChartLineCfg {
  d: readonly number[];
  alpha: readonly number[];
  width: readonly number[];
  /** Past this distance from rock the terrain program takes the early out. */
  earlyOutPx: number;
}

/**
 * One fleet, as the chart's pieces read it.
 *
 * It is a WIDER view than `FleetInfo`, which the sonar and the wake layers
 * take: those two need a place and a range, and the chart also needs the
 * order a hull is under, the target of that order and the two clocks a
 * reveal and a share run on. The two views stay separate on purpose —
 * `FleetInfo` is the hot path of two layers and must not grow a field for a
 * piece of furniture.
 */
export interface ChartFleet {
  id: number;
  faction: number;
  /** Where the hull is drawn now, in world px. */
  x: number; y: number;
  /** The reveal fade, 0 to 1. A contact still arriving rides under 0.3. */
  alpha: number;
  /** The smoothed heading, in radians. */
  heading: number;
  own: boolean;
  /** The hull belongs to a member of the viewer's own squad (R199). */
  squad: boolean;
  /** The account that owns the hull, or 0 where the wire withheld it. */
  ownerPlayer: number;
  /** The server withheld this contact's composition (§5). */
  classified: boolean;
  cls: number;
  /** The rendered speed in px per WALL second, smoothed (R101). */
  speed: number;
  /** The standing order (`Mission`). */
  mission: number;
  /** Where the order points, in world px. */
  tx: number; ty: number;
  /** The other end of a patrol lane, in world px. */
  px: number; py: number;
  /** The hull this one escorts, or 0. */
  escorting: number;
  /** The hostile this hull chases and fires at alone, or 0 (R544). */
  focus: number;
  /** The hostile the chase of this own hull follows now, the focus or the
   *  prey of an attack-move or a patrol, or 0 (R2754). */
  chase: number;
  /** The object a commit sails to, or 0 (R788, R1208). Own fleets only. */
  pledgeSite: number;
  /** The own fleet works a commission (R2833): its order wears the
   *  commission ink. */
  commissioned: boolean;
  /** The anchor state (`AnchorState`). */
  anchor: number;
  /** Listening range this viewer gets through the hull, px; 0 = none (R112). */
  sonarPx: number;
  /** Map seconds a scan reveal still runs for; 0 = no reveal (R412). */
  scanReadySec: number;
  /** This Oracle lends its array to the faction (R453). */
  sharing: boolean;
  /** The reach of the round the hull loaded, in px; 0 = none. */
  torpedoRange: number;
  /** The kind the next shot fires (R2017), or 0. */
  firingKind: number;
  /**
   * The owner's effect on this hull, a catalog wire id of
   * `data/contracts/effects.toml`; 0 = none (R910). The server resolves
   * the pick and the class-reveal gate.
   */
  effect: number;
  /** The NPC kind of a hull of the NPC side (`NpcKind`, R2637); 0 or
   *  absent for a hull of a faction. */
  npc?: number;
  /** An own hull sails a course of several legs (R2704): `tx`, `ty` is
   *  the leg it sails to now, and the mission names no lane. */
  course?: boolean;
}

/**
 * The one thing the chart asks of the fleet layer.
 *
 * `FleetLayer` satisfies it, so `MapScene` hands over the layer it already
 * holds and a test hands over a stub of three members.
 */
export interface ChartFleets {
  /**
   * Every fleet, in a buffer that is reused between calls (R131). A reader
   * must use it inside the frame and must not keep the reference.
   */
  chartFleets(): readonly ChartFleet[];
  /**
   * The dotted order lines of §6.1 are HANDED OVER for one pack (R548).
   *
   * The bearing-lines piece re-arms it on every frame it draws, and the
   * fleet layer's own pack clears it again, so a chart that stops drawing
   * gives the dotted lines back on the next frame.
   */
  ordersOff: boolean;
  /** The commander's own hold, for the gather, return and home legs. */
  readonly home: { x: number; y: number } | null;
}

/**
 * Everything a piece may read, in one object that is rewritten in place on
 * every frame (R131).
 */
export interface ChartContext {
  /** The reading being DRAWN. It flips at the middle of a fade. */
  reading: ChartReading;
  /** The sheet form factor (`isSheet()`), which is the `mobile` gate. */
  sheet: boolean;
  /** The camera scale: CSS px for one world px. */
  zoom: number;
  /**
   * The band scale of the four chart text steps at this zoom, in step order
   * (`steps.ts bandScalesInto`, R1796). The controller writes it once a
   * frame and both layers hand it to their quad batches as one uniform.
   */
  stepScale: Float32Array;
  /** The visible world rect. */
  view: ViewRect;
  /** `view` grown by the cull margin. */
  cull: ViewRect;
  /** The camera of this frame. */
  cam: CameraUniforms;
  /** Seconds since the loop started. */
  timeS: number;
  /** The cache of server truth. */
  state: GameState;
  /** The map, as the chart reads it. */
  scene: ChartScene;
  /** What is selected now, or null. */
  selection: Selection;
  /** What the pointer is over, or null. */
  hover: Hit | null;
  /** Where the pointer is over the canvas, in CSS px, or null. */
  hoverAt: { x: number; y: number } | null;
  /** The cell under the pointer, or −1 when the pointer is off the map. */
  hoverCell: number;
  /**
   * The id of the point of interest whose card the peek shows, 0 for none
   * (R2955). A point is a small mark and never a hit, so `hover` cannot
   * name it; card 69 outlines the area of a current gate from it.
   */
  peekPoi: number;
  /** Every ridge and every trench of this world, or null before the hello. */
  terrain: { ridges: Feature[]; trenches: Feature[] } | null;
  /** The drawn name of every feature, or null before the hello. */
  names: FeatureNames | null;
  /** The map text. A piece writes a label with one call. */
  glyphs: ChartGlyphs;
  /** The whole `[chart]` pack. A piece reads its own table out of it. */
  cfg: typeof CHART;
  /** The world clock in world seconds (`state.worldClockSec()`). */
  worldClockS: number;
  /** A replay film has the glass (R531). A `hide` piece is gated out. */
  filming: boolean;
  /** The armed aim point in WORLD px, or null when nothing is armed. */
  aim: { x: number; y: number } | null;
  /** Is the border band drawn? The graticule moves its figures for it. */
  bandOn: boolean;
  /**
   * The corner grid of the map edge (R1812, round 086 card 14): the clear
   * map and the slots the graticule skips, in CSS px of the canvas, or null
   * on a page with no chrome. The page reads it after a layout change.
   */
  edges: CornerGrid | null;
  /**
   * NEEDS YOU stands open in the INBOX (inbox.md §8, R1846): the INBOX is
   * the open panel and the group is not folded. The inbox pins (card 68)
   * draw only then. A page with no INBOX reads false.
   */
  needsPins: boolean;
  /**
   * The message whose inbox pin is lit, or 0 (inbox.md §8, R1846): the
   * NEEDS YOU item the mouse points at, the one that holds the keyboard
   * focus, or the one pressed last while the INBOX stands.
   */
  needsLit: number;
  /**
   * The radius of the harbour limits round each hold, in world px
   * (v2-the-deep.md §6.3, R2585): `CatalogRules.harbour_limits_px` while
   * the flag `modifiers` holds, and 0 otherwise. The page reads the flag,
   * so card 28 draws what it is handed. A page with no game reads 0.
   */
  harbourPx: number;
  /**
   * The world plays the NPC side (v2-the-deep.md §8, R2637): the page
   * writes the flag `pirates` here, so card 50 marks the narrows that a
   * pirate group hunts at, and draws what it is handed. A page with no
   * game reads false.
   */
  npcWater: boolean;
  /**
   * The world plays the treasure of v2 (§9, R2714): the page writes the
   * flag `treasure` here, so card 69 marks the seeded caches the survey
   * holds, and draws what it is handed. A page with no game reads false.
   */
  cachesOn: boolean;
  /**
   * The groups of chart pieces the reader hid in the VIEW panel (R1812),
   * as `setHidden` took them. The legend card shows its DEPTH and CURRENT
   * sections only while their groups show (R2616).
   */
  hiddenGroups: readonly string[];
}

/** One piece of furniture that draws on the canvas. */
export interface Piece {
  readonly id: ChartPieceId;
  readonly tier: ChartTier;
  readonly pack: ChartPack;
  readonly film: ChartFilm;
  /** The reasons this piece packs again for. */
  readonly rebuildOn: readonly RebuildReason[];
  /**
   * The piece's own gate, on top of the common one: a piece that waits for
   * a hover, a selection or a datum the wire has not sent yet.
   */
  visible?(ctx: ChartContext): boolean;
  /** Reads the world and builds whatever the piece draws from. */
  rebuild(ctx: ChartContext): void;
  /** A live piece's own per-frame advance, before it packs. */
  update?(ctx: ChartContext, frame: FrameInfo): void;
  /** Writes the instances of this piece into the sinks of its pack. */
  emit(sinks: ChartSinks, ctx: ChartContext, frame: FrameInfo): void;
  /**
   * A draw of the piece's OWN, after the six batch flushes of its tier.
   *
   * One piece needs it: the sonar sweep (card 59) is a per-pixel term on
   * one quad, which no batch can carry. `fade` is the reading crossfade the
   * layer holds, because the piece draws outside the batches that carry it
   * as a uniform. A piece that draws in the batches alone leaves this unset
   * and the layer calls nothing.
   */
  drawExtra?(ctx: DrawCtx, fade: number): void;
  /** What this piece has under a world point, or null. */
  pick?(wx: number, wy: number, tolPx: number): PickHit | null;
  /**
   * Offers the small mark of this piece whose drawn box, grown by
   * `margin` world px, holds a world point (R2851): a wreck or a point of
   * interest. It writes into `out` and allocates nothing. A small mark
   * answers no `pick`, so `commands.clickMap` never finds it and the
   * tooltip peeks it (`fast/smallmarks.ts`).
   */
  markPick?(wx: number, wy: number, margin: number, out: SmallMark): void;
  /**
   * The piece's own edge (R801): true when what it packed last no longer
   * stands for the world, so the controller builds it again this frame.
   * The controller asks it once a frame, and only while the piece passes
   * the gate. The wrecks (card 08) answer it: they pack the view grown by a
   * slack, and a pan past that slack or a battle mark that leaves its
   * window is an edge no reason names. It must be one compare, never a
   * walk of the world.
   */
  outgrown?(ctx: ChartContext): boolean;
  dispose?(): void;
}

/** One piece of furniture that is DOM: a card of words in the host. */
export interface DomPiece {
  readonly id: ChartPieceId;
  readonly tier: 'dom';
  readonly film: ChartFilm;
  readonly rebuildOn: readonly RebuildReason[];
  visible?(ctx: ChartContext): boolean;
  /** Builds the element and appends it to the `#furniture` host. */
  mount(root: HTMLElement, ctx: ChartContext): void;
  /** Writes this reading's words into the element. */
  render(ctx: ChartContext): void;
  /** Takes the element off the host again. */
  unmount(): void;
}

/** Either kind, which is what the registry holds. */
export type AnyPiece = Piece | DomPiece;

/** Is this a DOM piece? The one test the controller makes on a piece. */
export function isDomPiece(p: AnyPiece): p is DomPiece {
  return p.tier === 'dom';
}
